import React, { useState } from 'react';

import { Select } from '@aline/common';
import { FileWarning, GitMerge, Info } from 'lucide-react';
import type { DisplayMode } from 'src/components';
import {
  AlertsPopperAction,
  AlertsPopperButton,
  AlertsPopperRow,
} from 'src/components/generic/AlertsPopper';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';

interface DocumentsAlertsButtonProps {
  detectingRelationshipsCount: number;
  displayMode: DisplayMode;
  effectiveMissingMatchesCount: number;
  failedRelationshipsCount: number;
  missingMatchesOnly: boolean;
  onFilterToMissingMatches: () => void;
  onFilterToUnconfirmedRelationships: () => void;
  unconfirmedRelationshipsCount: number;
  unconfirmedRelationshipsOnly: boolean;
}

export function DocumentsAlertsButton(props: DocumentsAlertsButtonProps): JSX.Element | null {
  const {
    detectingRelationshipsCount,
    displayMode,
    effectiveMissingMatchesCount,
    failedRelationshipsCount,
    missingMatchesOnly,
    onFilterToMissingMatches,
    onFilterToUnconfirmedRelationships,
    unconfirmedRelationshipsCount,
    unconfirmedRelationshipsOnly,
  } = props;
  const [popperOpen, setPopperOpen] = useState(false);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryFailed] = Apollo.useRetryFailedDocumentRelationshipsMutation();

  const isMissingMatchesActive = missingMatchesOnly && displayMode === 'relationship';
  const isUnconfirmedActive = unconfirmedRelationshipsOnly && displayMode === 'relationship';
  const showUnconfirmed = isUnconfirmedActive || unconfirmedRelationshipsCount > 0;
  const showMissingMatches =
    (isMissingMatchesActive || effectiveMissingMatchesCount > 0) &&
    detectingRelationshipsCount === 0;
  const showDetecting = detectingRelationshipsCount > 0;
  const showFailed = failedRelationshipsCount > 0;

  const totalAlerts =
    (showUnconfirmed ? 1 : 0) +
    (showMissingMatches ? 1 : 0) +
    (showDetecting ? 1 : 0) +
    (showFailed ? 1 : 0);

  if (totalAlerts === 0) return null;

  return (
    <>
      <AlertsPopperButton
        buttonDataCy={Select.relationships.alertsButton}
        count={totalAlerts}
        icon={<Info size={18} />}
        label="Alerts"
        open={popperOpen}
        popperDataCy={Select.relationships.alertsPopper}
        onOpenChange={setPopperOpen}
      >
        {showDetecting && (
          <AlertsPopperRow
            dataCy={Select.relationships.detectingChip}
            icon={<PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
            subtitle="AI is scanning documents for relationships"
            title={`${detectingRelationshipsCount} detecting relationship${detectingRelationshipsCount === 1 ? '' : 's'}`}
          />
        )}

        {showUnconfirmed && (
          <AlertsPopperRow
            action={
              <AlertsPopperAction
                dataCy={Select.relationships.forReviewViewButton}
                onClick={() => {
                  setPopperOpen(false);
                  onFilterToUnconfirmedRelationships();
                }}
              >
                View
              </AlertsPopperAction>
            }
            dataCy={Select.relationships.forReviewChip}
            icon={<GitMerge size={20} />}
            subtitle="Found potential relationships between documents"
            title={`${unconfirmedRelationshipsCount} relationship${unconfirmedRelationshipsCount === 1 ? '' : 's'} for review`}
          />
        )}

        {showMissingMatches && (
          <AlertsPopperRow
            action={
              <AlertsPopperAction
                dataCy={Select.relationships.missingMatchesViewButton}
                onClick={() => {
                  setPopperOpen(false);
                  onFilterToMissingMatches();
                }}
              >
                View
              </AlertsPopperAction>
            }
            dataCy={Select.relationships.missingMatchesChip}
            icon={<FileWarning size={20} />}
            subtitle="Documents without a matched relationship"
            title={`${effectiveMissingMatchesCount} missing match${effectiveMissingMatchesCount === 1 ? '' : 'es'}`}
          />
        )}

        {showFailed && (
          <AlertsPopperRow
            action={
              <AlertsPopperAction
                dataCy={Select.relationships.failedRetryButton}
                onClick={() => {
                  setPopperOpen(false);
                  setRetryDialogOpen(true);
                }}
              >
                Retry
              </AlertsPopperAction>
            }
            dataCy={Select.relationships.failedChip}
            icon={<FileWarning size={20} />}
            subtitle="Retry to continue relationship detection"
            title={`${failedRelationshipsCount} failed relationship${failedRelationshipsCount === 1 ? '' : 's'}`}
          />
        )}
      </AlertsPopperButton>
      <ConfirmationDialog
        message={`${failedRelationshipsCount} document${failedRelationshipsCount === 1 ? '' : 's'} failed relationship detection. Would you like to retry?`}
        open={retryDialogOpen}
        primaryText="Retry"
        title="Retry failed relationships?"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={async () => {
          await retryFailed();
        }}
        onClose={() => setRetryDialogOpen(false)}
      />
    </>
  );
}
